export interface SearchWindowPage<T> {
  items?: T[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

export interface FilledSearchPage<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
}

interface FillSearchPageOptions<T> {
  cursor?: string | null;
  pageSize: number;
  fetchWindow: (cursor: string | null, limit: number) => Promise<SearchWindowPage<T>>;
  getKey: (item: T) => string;
  knownKeys?: Iterable<string>;
  onChunk?: (items: T[]) => void;
}

interface BufferedLiveSearchPagerOptions<T> {
  pageSize: number;
  fetchWindow: (cursor: string | null, limit: number) => Promise<SearchWindowPage<T>>;
  getKey: (item: T) => string;
}

/**
 * Builds one logical UI page from one or more bounded API windows. Every non-empty window can be
 * published immediately, while callers may omit onChunk to prepare the page in a hidden buffer.
 */
export async function fillLiveSearchPage<T>({
  cursor = null,
  pageSize,
  fetchWindow,
  getKey,
  knownKeys = [],
  onChunk,
}: FillSearchPageOptions<T>): Promise<FilledSearchPage<T>> {
  const items: T[] = [];
  const seen = new Set(knownKeys);
  let nextCursor = cursor ?? undefined;
  let hasMore = true;

  while (items.length < pageSize && hasMore) {
    const requestedCursor = nextCursor;
    const page = await fetchWindow(requestedCursor ?? null, pageSize - items.length);
    const chunk = (page.items ?? []).filter((item) => {
      const key = getKey(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (chunk.length > 0) {
      items.push(...chunk);
      onChunk?.(chunk);
    }

    hasMore = page.hasMore ?? false;
    nextCursor = page.nextCursor ?? undefined;
    if (hasMore && (!nextCursor || nextCursor === requestedCursor)) {
      throw new Error('Live search returned a non-advancing cursor.');
    }
  }

  return { items, nextCursor, hasMore };
}

/** Keeps exactly one logical page ahead without exposing it until the UI consumes it. */
export class BufferedLiveSearchPager<T> {
  private readonly options: BufferedLiveSearchPagerOptions<T>;
  private nextCursor?: string;
  private hasMore = true;
  private buffered?: FilledSearchPage<T>;
  private prefetchPromise?: Promise<FilledSearchPage<T>>;

  constructor(options: BufferedLiveSearchPagerOptions<T>) {
    this.options = options;
  }

  async loadFirst(onChunk: (items: T[]) => void): Promise<FilledSearchPage<T>> {
    const page = await this.fill(null, [], onChunk);
    this.commit(page);
    return page;
  }

  prefetch(
    knownKeys: Iterable<string>,
    onSettled?: (page: FilledSearchPage<T>) => void
  ): Promise<FilledSearchPage<T>> | undefined {
    if (!this.hasMore || !this.nextCursor) return undefined;
    if (this.buffered) return Promise.resolve(this.buffered);
    if (this.prefetchPromise) return this.prefetchPromise;

    const promise = this.fill(this.nextCursor, knownKeys);
    this.prefetchPromise = promise;
    void promise
      .then((page) => {
        this.buffered = page;
        onSettled?.(page);
      })
      .catch(() => {
        // Consumption retries this same cursor and surfaces an error only if the user reaches it.
      })
      .finally(() => {
        if (this.prefetchPromise === promise) this.prefetchPromise = undefined;
      });
    return promise;
  }

  async takeNext(knownKeys: Iterable<string>): Promise<FilledSearchPage<T> | undefined> {
    if (!this.hasMore) return undefined;

    const page = this.buffered ?? await this.prefetch(knownKeys);
    if (!page) return undefined;

    this.buffered = undefined;
    this.prefetchPromise = undefined;
    this.commit(page);
    return page;
  }

  private fill(
    cursor: string | null,
    knownKeys: Iterable<string>,
    onChunk?: (items: T[]) => void
  ) {
    return fillLiveSearchPage({
      cursor,
      pageSize: this.options.pageSize,
      fetchWindow: this.options.fetchWindow,
      getKey: this.options.getKey,
      knownKeys,
      onChunk,
    });
  }

  private commit(page: FilledSearchPage<T>) {
    this.nextCursor = page.nextCursor;
    this.hasMore = page.hasMore;
  }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
